import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileImageOutlined,
  FileTextOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  ShareAltOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import useAppConfig from "hooks/access-control/useAppConfig";
import { useFileUpload } from "hooks/useFileUpload";
import GooglePlacesAutocompleteInput from "common/GooglePlacesAutocompleteInput";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import SignInRequiredModal from "components/auth/SignInRequiredModal";
import RichTextEditor from "components/common/RichTextEditor";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { bearerGuestToken, ensureGuestSession } from "utils/guestSession";
import {
  locationFieldsFromPlaceDetails,
  regionSummaryFromPlaceDetails,
} from "utils/googlePlaces";
import { hasRichText, richTextMarkup } from "./richTextUtils";
import {
  EDUCATION_LEVELS,
  SHIFTS,
  blankProfile,
  getSectorCode,
  getSectorName,
  normalizeProfile,
  profileCompletion,
  profileTitle,
  sectionForKey,
  titleCase,
  toList,
} from "./professionalProfileUtils";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;
const EDUCATION_HISTORY_LEVELS = [
  "High School",
  "Vocational Certificate",
  "Diploma / Advanced Diploma",
  "Associate Degree",
  "Bachelor's Degree",
  "Postgraduate Certificate",
  "Master's Degree",
  "Doctorate",
  "Professional Certification",
  "Other",
];
const EDUCATION_STATUSES = ["completed", "ongoing", "paused", "Withdrawn"];

const profileOwnerId = (profile = {}) =>
  [
    profile.userId,
    profile.userID,
    profile.ownerUserId,
    profile.profileUserId,
    profile.userProfileId,
    profile.professionalUserId,
    profile.professionUserId,
    profile.createdBy,
    profile.createdByUserId,
    profile.user?.id,
    profile.owner?.id,
    profile.profileUser?.id,
    profile.userProfile?.id,
    profile.userProfile?.userId,
    profile.professionalUser?.id,
  ]
    .map((value) => String(value || "").trim())
    .find(Boolean) || "";

const authUserIds = (user = {}) =>
  [
    user.id,
    user.userId,
    user.uid,
    user.profileId,
    user.user?.id,
    user.profile?.id,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

const FIELD_CONFIG = {
  previousRegionsWorkedIn: {
    title: "Previous Regions",
    empty: "No previous regions added",
    help: "Add places like city, state, country. Avoid full street addresses.",
    fields: [{ name: "region", label: "Region", placeholder: "e.g., Los Angeles, CA, USA", required: true, locationSearch: "region" }],
    toItem: (values) => values.region,
    itemTitle: (item) => item,
    itemMeta: () => "",
  },
  references: {
    title: "References",
    empty: "No references added",
    fields: [
      { name: "name", label: "Name", placeholder: "e.g., Grace Johnson", required: true },
      { name: "relationship", label: "Relationship", placeholder: "e.g., Former manager, supervisor, client" },
      { name: "phone", label: "Phone", placeholder: "e.g., +1 555 010 0100" },
      { name: "email", label: "Email", placeholder: "e.g., grace@example.com" },
      { name: "notes", label: "Notes", placeholder: "Add any useful context about this reference.", multiline: true },
    ],
    itemTitle: (item) => item.name || "Reference",
    itemMeta: (item) => [item.relationship, item.phone, item.email].filter(Boolean).join(" • "),
  },
  workExperienceHistory: {
    title: "Work Experience",
    empty: "No work experience added",
    help: "Search or enter the city, state, country where the work happened.",
    fields: [
      { name: "organization", label: "Organization", placeholder: "e.g., Olive Care Services" },
      { name: "role", label: "Role", placeholder: "e.g., Senior Caregiver" },
      { name: "location", label: "Location", placeholder: "e.g., Los Angeles, CA, USA", locationSearch: "region" },
      { name: "startDate", label: "Start Date", placeholder: "Select start date", inputType: "date" },
      { name: "endDate", label: "End Date", placeholder: "Select end date", inputType: "date", hideWhenCurrent: true },
      { name: "isCurrent", label: "Currently work here", checkbox: true },
      { name: "notes", label: "Notes", placeholder: "Summarize responsibilities, achievements, or scope.", multiline: true },
    ],
    itemTitle: (item) => item.role || item.organization || "Work Experience",
    itemMeta: (item) => [item.organization, item.location, item.isCurrent || item.current ? "Current" : item.endDate].filter(Boolean).join(" • "),
  },
  education: {
    title: "Education",
    empty: "No education added",
    fields: [
      { name: "institution", label: "Institution", placeholder: "e.g., University of California", required: true },
      { name: "program", label: "Field of study", placeholder: "e.g., Nursing, Accounting, Computer Science" },
      { name: "educationLevel", label: "Education Level", options: EDUCATION_HISTORY_LEVELS, placeholder: "Select education level" },
      { name: "graduationYear", label: "Graduation Year", placeholder: "e.g., 2024", inputType: "year" },
      { name: "startDate", label: "Start Date", placeholder: "Select start date", inputType: "date" },
      { name: "endDate", label: "End Date", placeholder: "Select end date", inputType: "date" },
      { name: "status", label: "Status", options: EDUCATION_STATUSES, placeholder: "Select status" },
      { name: "notes", label: "Notes", placeholder: "Add honors, coursework, or extra education context.", multiline: true },
    ],
    itemTitle: (item) => item.institution || "Education",
    itemMeta: (item) => [item.program, item.educationLevel || item.degree, item.status].filter(Boolean).join(" • "),
  },
  certifications: {
    title: "Certifications",
    empty: "No certifications added",
    help: "Upload the certification file when available. The saved profile stores the uploaded file ID.",
    fields: [
      { name: "name", label: "Certification Name", placeholder: "e.g., Certified Nursing Assistant", required: true },
      { name: "certificationNumber", label: "Certification Number", placeholder: "e.g., CNA-123456" },
      { name: "issuer", label: "Issuer", placeholder: "e.g., California Department of Public Health" },
      { name: "issueDate", label: "Issue Date", placeholder: "Select issue date", inputType: "date" },
      { name: "expiryDate", label: "Expiry Date", placeholder: "Select expiry date", inputType: "date" },
      {
        name: "fileId",
        label: "Certificate File",
        placeholder: "No certificate uploaded yet",
        upload: "document",
        uploadLabel: "Upload certificate",
      },
    ],
    itemTitle: (item) => item.name || "Certification",
    itemMeta: (item) => [item.issuer, item.certificationNumber, item.expiryDate].filter(Boolean).join(" • "),
  },
  licenses: {
    title: "Licenses",
    empty: "No licenses added",
    help: "Upload the license file when available. Licenses are handled like certifications for verification.",
    fields: [
      { name: "name", label: "License Name", placeholder: "e.g., Registered Nurse License", required: true },
      { name: "certificationNumber", label: "License Number", placeholder: "e.g., RN-123456" },
      { name: "issuer", label: "Issuer", placeholder: "e.g., California Board of Registered Nursing" },
      { name: "issueDate", label: "Issue Date", placeholder: "Select issue date", inputType: "date" },
      { name: "expiryDate", label: "Expiry Date", placeholder: "Select expiry date", inputType: "date" },
      {
        name: "fileId",
        label: "License File",
        placeholder: "No license uploaded yet",
        upload: "document",
        uploadLabel: "Upload license",
      },
    ],
    itemTitle: (item) => item.name || "License",
    itemMeta: (item) => [item.issuer, item.certificationNumber, item.expiryDate].filter(Boolean).join(" • "),
  },
  interestedLocations: {
    title: "Interested Locations",
    empty: "No interested locations added",
    help: "Search or enter places like city, county, state, country.",
    fields: [
      { name: "city", label: "City", placeholder: "e.g., Los Angeles" },
      { name: "state", label: "State", placeholder: "e.g., CA" },
      { name: "county", label: "County", placeholder: "Optional" },
      { name: "country", label: "Country", placeholder: "e.g., USA" },
      { name: "open", label: "Open to this location", checkbox: true },
    ],
    itemTitle: (item) => [item.city, item.state].filter(Boolean).join(", ") || item.county || "Location",
    itemMeta: (item) => [item.county, item.country, item.open === false ? "Limited" : "Open"].filter(Boolean).join(" • "),
  },
  documents: {
    title: "Documents",
    empty: "No documents added",
    help: "Add one document by camera, gallery, or file upload, then enter its caption.",
    fields: [
      { name: "caption", label: "Caption", placeholder: "Describe this document", required: true },
      {
        name: "id",
        label: "Document File",
        placeholder: "No document uploaded yet",
        upload: "document",
        uploadLabel: "Upload document",
        required: true,
      },
    ],
    itemTitle: (item) => item.caption || item.fileName || item.id || "Document",
    itemMeta: (item) => [item.fileName, item.id ? "Document attached" : ""].filter(Boolean).join(" • "),
  },
  photos: {
    title: "Photos",
    empty: "No photos added",
    help: "Add photos by camera or gallery. To add a captioned document, use Upload Document.",
    fields: [
      {
        name: "url",
        label: "Photo",
        placeholder: "No photo uploaded yet",
        upload: "photo",
        uploadLabel: "Upload photo",
        required: true,
      },
    ],
    toItem: (values) => values.url,
    itemTitle: (item) => (typeof item === "string" ? item : item.caption || item.fileName || item.url || "Photo"),
    itemMeta: (item) => (typeof item === "string" ? "" : [item.fileName, item.url].filter(Boolean).join(" • ")),
  },
};

const compactObject = (values) =>
  Object.fromEntries(
    Object.entries(values || {}).filter(([, value]) => {
      if (typeof value === "string") return value.trim();
      return value !== undefined && value !== null;
    }).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]),
  );

const sectorOptionValue = (sector) => sector.code || sector.id || "";
const sectorFromCode = (code) => ({ code, name: titleCase(code) });
const serviceLabel = (service) => typeof service === "string"
  ? service
  : service?.name || service?.briefName || service?.code || service?.id;
const serviceValue = (service) => typeof service === "string"
  ? service
  : service?.id || service?.code || service?.name;
const completionColor = (completion) => {
  if (completion <= 50) return "#d90101";
  if (completion <= 75) return "#f59e0b";
  return "#27ae30";
};
const locationLabel = (location) => {
  if (!location) return "";
  if (typeof location === "string") return location.trim();
  const direct = String(location.label || location.location || location.name || location.address || location.formattedAddress || "").trim();
  if (direct) return direct;
  return [location.city, location.county, location.state, location.country].filter(Boolean).join(", ");
};
const uniqueLocationOptions = (items) => {
  const seen = new Set();
  return toList(items)
    .map(locationLabel)
    .filter(Boolean)
    .filter((item) => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((value) => ({ value, label: value }));
};
const locationFieldsFromLabel = (label) => {
  const parts = String(label || "").split(",").map((item) => item.trim()).filter(Boolean);
  if (!parts.length) return {};
  const countryNames = new Set(["us", "usa", "u.s.", "u.s.a.", "united states", "united states of america"]);
  const usStates = new Set([
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "IA", "ID", "IL", "IN", "KS",
    "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE", "NH", "NJ", "NM",
    "NV", "NY", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WA", "WI",
    "WV", "WY", "DC",
  ]);
  const looksLikeCountry = (value) => countryNames.has(String(value || "").trim().toLowerCase());
  const countryForState = (value) => (usStates.has(String(value || "").trim().toUpperCase()) ? "USA" : "");
  if (parts.length === 1) return looksLikeCountry(parts[0]) ? { country: parts[0] } : { city: parts[0] };
  if (parts.length === 2) {
    if (looksLikeCountry(parts[1])) return { state: parts[0], country: parts[1] };
    return { city: parts[0], state: parts[1], country: countryForState(parts[1]) };
  }
  if (parts.length === 3) return { city: parts[0], state: parts[1], country: parts[2] };
  return {
    city: parts.slice(0, -3).join(", "),
    county: parts[parts.length - 3]?.replace(/\s+County$/i, ""),
    state: parts[parts.length - 2],
    country: parts[parts.length - 1],
  };
};
const selectedSectorCodes = (profile) => {
  const fromCodes = toList(profile.sectorCodes).filter((code) => code && code !== "GENERAL");
  if (fromCodes.length) return Array.from(new Set(fromCodes));
  const fromSectors = toList(profile.sectors)
    .map((sector) => getSectorCode(sector))
    .filter((code) => code && code !== "GENERAL");
  return Array.from(new Set(fromSectors));
};

const OPENABLE_MEDIA_PATTERN = /^(https?:|data:|blob:|file:|content:)/i;
const isOpenableMediaUrl = (value) => OPENABLE_MEDIA_PATTERN.test(String(value || "").trim());

const extractFileId = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return isOpenableMediaUrl(text) ? "" : text;
};

const profileDocumentForRequest = (document) => {
  if (typeof document === "string") {
    const id = extractFileId(document);
    return id ? { id } : null;
  }
  if (!document || typeof document !== "object") return null;
  const id = extractFileId(document.id || document.fileId || document.uuid || document.documentId);
  if (!id) return null;
  return compactObject({
    id,
    caption: document.caption || document.name || document.fileName,
  });
};

const profilePhotoForRequest = (photo) => {
  if (typeof photo === "string") return extractFileId(photo);
  if (!photo || typeof photo !== "object") return "";
  return extractFileId(photo.fileId || photo.id || photo.uuid || photo.photoId);
};

const profileCertificationForRequest = (certification) => {
  if (!certification || typeof certification !== "object") return certification;
  return compactObject({
    credentialType: certification.credentialType || certification.type,
    name: certification.name,
    certificationNumber: certification.certificationNumber,
    issuer: certification.issuer,
    issueDate: certification.issueDate,
    expiryDate: certification.expiryDate,
    fileId: extractFileId(certification.fileId || certification.id || certification.uuid),
  });
};

const buildProfileRequest = (profile) => {
  const sectorCode = getSectorCode(profile.sector || profile.sectorCode);
  const generalProfile = profile.profileType === "GENERAL" || sectorCode === "GENERAL";
  const sector = generalProfile
    ? sectorFromCode("GENERAL")
    : typeof profile.sector === "string"
      ? sectorFromCode(profile.sector)
      : profile.sector || sectorFromCode(sectorCode);
  const selectedCodes = generalProfile
    ? selectedSectorCodes(profile)
    : sectorCode && sectorCode !== "GENERAL"
      ? [sectorCode]
      : [];
  const selectedSectors = selectedCodes.map((code) => {
    const existing = toList(profile.sectors).find((item) => getSectorCode(item) === code);
    return typeof existing === "string" ? sectorFromCode(existing) : existing || sectorFromCode(code);
  });

  const photoIds = Array.from(new Set([
    ...toList(profile.photoFileIds).map(extractFileId),
    ...toList(profile.photos).map(profilePhotoForRequest),
  ].filter(Boolean)));

  const certifications = [
    ...toList(profile.certifications).map((item) => ({ ...item, credentialType: item?.credentialType || "CERTIFICATION" })),
    ...toList(profile.licenses).map((item) => ({ ...item, credentialType: "LICENSE" })),
  ];

  return {
    ...(profile || {}),
    sectorCode,
    sector,
    sectorCodes: selectedCodes,
    sectors: selectedSectors,
    profileType: generalProfile ? "GENERAL" : "SPECIALIZED",
    documents: toList(profile.documents).map(profileDocumentForRequest).filter(Boolean),
    photos: photoIds,
    certifications: certifications.map(profileCertificationForRequest).filter(Boolean),
  };
};

export default function ProfessionalProfileDetails({
  forceAddMode = false,
  startEditing = false,
  profileIdOverride = "",
  profileDataOverride = null,
  embedded = false,
  hidePromotions = false,
  readOnly = false,
}) {
  const navigate = useNavigate();
  const { profileId: routeProfileId } = useParams();
  const profileId = profileIdOverride || routeProfileId;
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const { appConfig } = useAppConfig();
  const [guestSession, setGuestSession] = useState(null);
  const actorId = user?.id || user?.userId || guestSession?.userId || guestSession?.sessionId || "";
  const hasProfileDataOverride = Boolean(profileDataOverride && typeof profileDataOverride === "object");
  const guestAuthToken = isAuthenticated ? "" : bearerGuestToken(guestSession);
  const urlRequestsEditMode = ["1", "true", "yes"].includes(String(searchParams.get("edit") || "").toLowerCase());
  const [profile, setProfile] = useState(() => hasProfileDataOverride
    ? normalizeProfile(profileDataOverride, selectedWorkspaceId)
    : blankProfile(selectedWorkspaceId));
  const [sectors, setSectors] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(Boolean(profileId) && !hasProfileDataOverride);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(!readOnly && Boolean(forceAddMode || startEditing || urlRequestsEditMode));
  const [error, setError] = useState("");
  const [signInPromptOpen, setSignInPromptOpen] = useState(false);
  const [entryConfig, setEntryConfig] = useState(null);
  const [entryIndex, setEntryIndex] = useState(null);
  const [fieldUpdateKind, setFieldUpdateKind] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [fileMetadataById, setFileMetadataById] = useState({});
  const [popularLocationOptions, setPopularLocationOptions] = useState([]);
  const [entryLocationQuery, setEntryLocationQuery] = useState("");
  const [stagedEntryUpload, setStagedEntryUpload] = useState(null);
  const stagedEntryUploadRef = useRef(null);
  const [entryForm] = Form.useForm();
  const [fieldUpdateForm] = Form.useForm();
  const [metadataForm] = Form.useForm();
  const { uploadFile, getFileMetadata, getFileSignedUrl, uploading: fileUploading } = useFileUpload();
  const isNew = !hasProfileDataOverride && (forceAddMode || !profileId);
  const completion = profileCompletion(profile);
  const profileCompletionColor = completionColor(completion);
  const isGeneral = getSectorCode(profile.sector || profile.sectorCode) === "GENERAL";
  const ownerId = profileOwnerId(profile);
  const canEditProfile = Boolean(isNew || (ownerId && authUserIds(user).includes(ownerId)));
  const profileEditMode = editMode && canEditProfile;
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const requireSignIn = () => setSignInPromptOpen(true);

  useEffect(() => {
    if (readOnly) {
      setEditMode(false);
      return;
    }
    if (urlRequestsEditMode) setEditMode(true);
  }, [readOnly, urlRequestsEditMode]);

  useEffect(() => {
    if (!isNew && !loading && !canEditProfile) setEditMode(false);
  }, [canEditProfile, isNew, loading]);

  useEffect(() => {
    if (hasProfileDataOverride) return undefined;
    let cancelled = false;
    const initializeGuest = async () => {
      if (isAuthenticated) {
        setGuestSession(null);
        return;
      }
      try {
        const session = await ensureGuestSession();
        if (!cancelled) setGuestSession(session || null);
      } catch {
        if (!cancelled) setGuestSession(null);
      }
    };
    initializeGuest();
    return () => {
      cancelled = true;
    };
  }, [hasProfileDataOverride, isAuthenticated]);

  const loadOptions = useCallback(async () => {
    if (hasProfileDataOverride) return;
    if (!actorId) return;
    try {
      const [sectorResult, serviceResult] = await Promise.all([
        olreadiaService.professionProfiles.listSectors(actorId),
        olreadiaService.professionProfiles.listServiceOfferings(actorId),
      ]);
      setSectors(toList(sectorResult));
      setServices(toList(serviceResult).filter((service) => service.active !== false));
    } catch {
      setSectors([]);
      setServices([]);
    }
  }, [actorId, hasProfileDataOverride]);

  const loadProfile = useCallback(async () => {
    if (hasProfileDataOverride) {
      const next = normalizeProfile(profileDataOverride, selectedWorkspaceId);
      setProfile(next);
      metadataForm.setFieldsValue(profileToMetadata(next));
      setLoading(false);
      setError("");
      return;
    }
    const sector = searchParams.get("sector");
    if (isNew) {
      if (!actorId) {
        setSignInPromptOpen(true);
        setLoading(false);
        return;
      }
      const next = normalizeProfile({
        ...blankProfile(selectedWorkspaceId),
        ...(sector ? {
          sector: sectorFromCode(sector),
          sectorCode: sector,
          profileType: sector === "GENERAL" ? "GENERAL" : "SPECIALIZED",
          profileName: sector === "GENERAL" ? "General Professional Profile" : "",
        } : {}),
      }, selectedWorkspaceId);
      setProfile(next);
      metadataForm.setFieldsValue(profileToMetadata(next));
      setLoading(false);
      return;
    }
    if (!isAuthenticated && !guestAuthToken) return;
    try {
      setLoading(true);
      setError("");
      const result = await olreadiaService.professionProfiles.get(profileId, guestAuthToken);
      const next = normalizeProfile(result, selectedWorkspaceId);
      setProfile(next);
      metadataForm.setFieldsValue(profileToMetadata(next));
    } catch (nextError) {
      setError(nextError?.message || "Could not load professional profile.");
    } finally {
      setLoading(false);
    }
  }, [actorId, guestAuthToken, hasProfileDataOverride, isAuthenticated, isNew, metadataForm, profileDataOverride, profileId, searchParams, selectedWorkspaceId]);

  useEffect(() => {
    loadOptions();
    loadProfile();
  }, [loadOptions, loadProfile]);

  useEffect(() => {
    let cancelled = false;
    const loadPopularLocations = async () => {
      if (hasProfileDataOverride) return;
      if (!isAuthenticated && !guestAuthToken) return;
      try {
        const locations = await olreadiaService.appStart.popularLocations({
          actorId,
          authToken: guestAuthToken,
          size: 50,
        });
        if (!cancelled) setPopularLocationOptions(uniqueLocationOptions(locations));
      } catch {
        if (!cancelled) setPopularLocationOptions([]);
      }
    };

    loadPopularLocations();
    return () => {
      cancelled = true;
    };
  }, [actorId, guestAuthToken, hasProfileDataOverride, isAuthenticated]);

  const sectorOptions = useMemo(() => sectors.map((sector) => ({
    value: sectorOptionValue(sector),
    label: getSectorName(sector),
  })).filter((option) => option.value), [sectors]);

  const serviceOptions = useMemo(() => {
    const optionsByValue = new Map();
    [...services, ...toList(profile.interestedServices)].forEach((service) => {
      const value = serviceValue(service);
      if (value) optionsByValue.set(value, { value, label: serviceLabel(service) });
    });
    return [...optionsByValue.values()];
  }, [profile.interestedServices, services]);

  const stageEntryUpload = useCallback((nextUpload) => {
    stagedEntryUploadRef.current = nextUpload;
    setStagedEntryUpload(nextUpload);
  }, []);

  const saveProfile = async (nextProfile = profile, section = "metadata", options = {}) => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    try {
      setSaving(true);
      const payload = buildProfileRequest({
        ...nextProfile,
        workspaceId: nextProfile.workspaceId || selectedWorkspaceId || null,
      });
      if (payload.profileType === "SPECIALIZED" && payload.sectorCodes.length !== 1) {
        message.warning("Select exactly one line of profession for this specialized profile.");
        return;
      }
      const saved = nextProfile.id
        ? await olreadiaService.professionProfiles.patchSection(section, payload, actorId, actorId, { previousProfile: profile })
        : await olreadiaService.professionProfiles.saveForUser(actorId, payload, actorId);
      const normalized = normalizeProfile(saved || payload, selectedWorkspaceId);
      const fallbackSectionList = toList(nextProfile?.[section]);
      if (
        ["documents", "photos", "certifications", "licenses"].includes(section) &&
        fallbackSectionList.length > 0 &&
        toList(normalized?.[section]).length === 0
      ) {
        normalized[section] = fallbackSectionList;
        if (section === "photos") {
          normalized.photoFileIds = toList(nextProfile.photoFileIds);
        }
      }
      setProfile(normalized);
      metadataForm.setFieldsValue(profileToMetadata(normalized));
      if (isNew && normalized.id) {
        navigate(paths.professionalProfileDetails(normalized.id), { replace: true });
      }
      if (options.showSuccess !== false) {
        message.success(options.successMessage || "Professional profile saved.");
      }
    } catch (nextError) {
      message.error(nextError?.message || "Could not save professional profile.");
    } finally {
      setSaving(false);
    }
  };

  const saveMetadata = async () => {
    const values = await metadataForm.validateFields();
    const selectedSector = sectors.find((sector) => sectorOptionValue(sector) === values.sectorCode) || sectorFromCode(values.sectorCode);
    const next = normalizeProfile({
      ...profile,
      ...values,
      sector: selectedSector,
      sectorCode: values.sectorCode,
      profileType: values.sectorCode === "GENERAL" ? "GENERAL" : "SPECIALIZED",
    }, selectedWorkspaceId);
    await saveProfile(next, "metadata");
    setEditMode(false);
  };

  const cancelEdit = async () => {
    setEditMode(false);
    setEntryConfig(null);
    setEntryIndex(null);
    closeFieldUpdate();
    if (isNew) {
      const sector = searchParams.get("sector");
      const next = normalizeProfile({
        ...blankProfile(selectedWorkspaceId),
        ...(sector ? {
          sector: sectorFromCode(sector),
          sectorCode: sector,
          profileType: sector === "GENERAL" ? "GENERAL" : "SPECIALIZED",
          profileName: sector === "GENERAL" ? "General Professional Profile" : "",
        } : {}),
      }, selectedWorkspaceId);
      setProfile(next);
      metadataForm.setFieldsValue(profileToMetadata(next));
      return;
    }
    await loadProfile();
  };

  const openFieldUpdate = (kind) => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    setFieldUpdateKind(kind);
    const values = profileToMetadata(profile);
    fieldUpdateForm.setFieldsValue({ [kind]: values[kind] });
    setTimeout(() => fieldUpdateForm.setFieldsValue({ [kind]: values[kind] }), 0);
  };

  const closeFieldUpdate = () => {
    setFieldUpdateKind(null);
    fieldUpdateForm.resetFields();
  };

  const saveFieldUpdate = async () => {
    const values = await fieldUpdateForm.validateFields();
    const value = values[fieldUpdateKind];
    let patch = { [fieldUpdateKind]: value };
    if (fieldUpdateKind === "sectorCode") {
      const selectedSector = sectors.find((sector) => sectorOptionValue(sector) === value) || sectorFromCode(value);
      patch = {
        sector: selectedSector,
        sectorCode: value,
        profileType: value === "GENERAL" ? "GENERAL" : "SPECIALIZED",
      };
    }
    const next = normalizeProfile({ ...profile, ...patch }, selectedWorkspaceId);
    await saveProfile(next, "metadata");
    closeFieldUpdate();
  };

  const updateList = async (key, nextList) => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    const next = normalizeProfile({ ...profile, [key]: nextList }, selectedWorkspaceId);
    setProfile(next);
    await saveProfile(next, sectionForKey(key));
  };

  const openEntryModal = (key, item, index) => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return null;
    }
    setEntryConfig(key);
    setEntryIndex(typeof index === "number" ? index : null);
    const config = FIELD_CONFIG[key];
    const baseDraft = key === "interestedLocations" ? { open: true } : {};
    const itemDraft = key === "previousRegionsWorkedIn" && typeof item === "string"
      ? { region: item }
      : typeof item === "string"
        ? { url: item }
        : item || {};
    if (key === "workExperienceHistory" && itemDraft.current && itemDraft.isCurrent === undefined) {
      itemDraft.isCurrent = itemDraft.current;
    }
    if (key === "education" && itemDraft.degree && !itemDraft.educationLevel) {
      itemDraft.educationLevel = itemDraft.degree;
    }
    const nextDraft = { ...baseDraft, ...itemDraft };
    if (key === "previousRegionsWorkedIn") {
      setEntryLocationQuery(nextDraft.region || "");
    } else if (key === "workExperienceHistory") {
      setEntryLocationQuery(nextDraft.location || "");
    } else if (key === "interestedLocations") {
      setEntryLocationQuery(locationLabel(nextDraft));
    } else {
      setEntryLocationQuery("");
    }
    const uploadField = config.fields.find((field) => field.upload);
    stageEntryUpload(uploadField && nextDraft?.[uploadField.name]
      ? {
          fieldName: uploadField.name,
          fileId: String(nextDraft[uploadField.name]),
          files: [{ fileId: String(nextDraft[uploadField.name]) }],
          fileName: nextDraft.fileName || nextDraft.caption || nextDraft.name || "",
          mimeType: nextDraft.mimeType || "",
          uploadedAt: nextDraft.uploadedAt || "",
        }
      : null);
    entryForm.setFieldsValue(nextDraft);
    Modal.destroyAll();
    setTimeout(() => entryForm.setFieldsValue(nextDraft), 0);
    return config;
  };

  const closeEntryModal = () => {
    setEntryConfig(null);
    setEntryIndex(null);
    setEntryLocationQuery("");
    stageEntryUpload(null);
    entryForm.resetFields();
  };

  const saveEntry = async () => {
    const config = FIELD_CONFIG[entryConfig];
    const uploadField = config.fields.find((field) => field.upload);
    if (uploadField && fileUploading) {
      message.warning("Please wait for the file upload to finish before saving.");
      return;
    }
    const formValues = await entryForm.validateFields();
    const stagedFiles = uploadField && stagedEntryUpload?.fieldName === uploadField.name
      ? toList(stagedEntryUpload.files)
      : [];
    let values = compactObject({
      ...formValues,
      ...(uploadField && stagedEntryUpload?.fieldName === uploadField.name
        ? {
            [uploadField.name]: stagedEntryUpload.fileId || stagedFiles[0]?.fileId,
            fileName: stagedEntryUpload.fileName,
            mimeType: stagedEntryUpload.mimeType,
            uploadedAt: stagedEntryUpload.uploadedAt,
          }
        : {}),
    });
    if (entryConfig === "interestedLocations" && entryLocationQuery) {
      const parsedLocation = locationFieldsFromLabel(entryLocationQuery);
      values = compactObject({
        ...values,
        city: values.city || parsedLocation.city,
        county: values.county || parsedLocation.county,
        state: values.state || parsedLocation.state,
        country: values.country || parsedLocation.country,
      });
      entryForm.setFieldsValue(values);
    }
    if (entryConfig === "workExperienceHistory" && !values.organization && !values.role) {
      message.warning("Enter an organization or role.");
      return;
    }
    if (["certifications", "licenses"].includes(entryConfig) && !values.name && !values.certificationNumber) {
      message.warning(entryConfig === "licenses" ? "Enter a license name or number." : "Enter a certification name or number.");
      return;
    }
    if (entryConfig === "interestedLocations" && !values.country) {
      message.warning("Enter a country before adding the location.");
      return;
    }
    const photoItems = entryConfig === "photos" && stagedFiles.length
      ? stagedFiles.map((stagedFile) => stagedFile.fileId).filter(Boolean)
      : null;
    const item = entryConfig === "workExperienceHistory" && values.isCurrent
      ? { ...values, endDate: undefined }
      : photoItems
        ? null
      : entryConfig === "certifications"
        ? { ...values, credentialType: "CERTIFICATION" }
        : entryConfig === "licenses"
          ? { ...values, credentialType: "LICENSE" }
          : config.toItem
            ? config.toItem(values)
            : values;
    const current = toList(profile[entryConfig]);
    const nextList = photoItems
      ? entryIndex == null
        ? [...current, ...photoItems]
        : current.flatMap((existing, index) => (index === entryIndex ? photoItems : [existing]))
      : entryIndex == null
        ? [...current, item]
        : current.map((existing, index) => (index === entryIndex ? item : existing));
    closeEntryModal();
    await updateList(entryConfig, nextList);
  };

  const itemForDeletedEntryFile = (key, item, index) => {
    if (key === "photos") {
      const fileId = extractFileId(toList(profile.photoFileIds)[index]) || mediaFileId(item);
      return fileId || null;
    }
    if (key === "documents") {
      const fileId = mediaFileId(item);
      return fileId ? { id: fileId } : null;
    }
    if (key === "certifications") {
      const fileId = mediaFileId(item);
      return fileId ? { fileId } : null;
    }
    if (key === "licenses") {
      const fileId = mediaFileId(item);
      return fileId ? { fileId, credentialType: "LICENSE" } : null;
    }
    return null;
  };

  const removeEntry = async (key, index) => {
    const current = toList(profile[key]);
    const removedItem = current[index];
    const nextList = current.filter((_, itemIndex) => itemIndex !== index);
    if (["documents", "photos", "certifications", "licenses"].includes(key) && profile.id) {
      const deleteItem = itemForDeletedEntryFile(key, removedItem, index);
      if (!deleteItem) {
        message.error("Could not identify the uploaded file to remove.");
        return;
      }
      const nextPhotoFileIds = key === "photos"
        ? toList(profile.photoFileIds).filter((_, itemIndex) => itemIndex !== index)
        : profile.photoFileIds;
      const next = normalizeProfile({
        ...profile,
        [key]: nextList,
        photoFileIds: nextPhotoFileIds,
      }, selectedWorkspaceId);
      setProfile(next);
      try {
        setSaving(true);
        const deleteProfile = {
          ...profile,
          [key]: [deleteItem],
          photoFileIds: key === "photos" ? [deleteItem] : profile.photoFileIds,
          workspaceId: profile.workspaceId || selectedWorkspaceId || null,
        };
        const payload = ["certifications", "licenses"].includes(key)
          ? {
              ...deleteProfile,
              certifications: [{ ...deleteItem, credentialType: key === "licenses" ? "LICENSE" : "CERTIFICATION" }],
              licenses: [],
            }
          : buildProfileRequest(deleteProfile);
        const saved = await olreadiaService.professionProfiles.patchSection(key, payload, actorId, actorId, {
          mediaPatchMode: "DELETE",
        });
        const normalized = normalizeProfile(saved || next, selectedWorkspaceId);
        setProfile(normalized);
        metadataForm.setFieldsValue(profileToMetadata(normalized));
        message.success("File removed.");
      } catch (removeError) {
        setProfile(profile);
        message.error(removeError?.message || "Could not remove file.");
      } finally {
        setSaving(false);
      }
      return;
    }
    await updateList(key, nextList);
  };

  const selectedServiceValues = toList(profile.interestedServices).map(serviceValue).filter(Boolean);

  const setServicesForProfile = async (values) => {
    const selected = values.map((value) => services.find((service) => serviceValue(service) === value) || { id: value, name: value });
    await updateList("interestedServices", selected);
  };

  const setShiftsForProfile = async (values) => {
    await updateList("interestedShifts", values);
  };

  const resolveRegionLocation = ({ details, prediction }, fieldName) => {
    const resolved = regionSummaryFromPlaceDetails(details) || prediction?.description || "";
    entryForm.setFieldsValue({ [fieldName]: resolved });
    setEntryLocationQuery(resolved);
  };

  const resolveInterestedLocation = ({ details, prediction }) => {
    const fromLabel = locationFieldsFromLabel(prediction?.description);
    const fromDetails = details
      ? locationFieldsFromPlaceDetails(details)
      : {};
    const next = {
      ...fromLabel,
      ...Object.fromEntries(Object.entries(fromDetails).filter(([, value]) => value)),
    };
    const query = regionSummaryFromPlaceDetails(details) || prediction?.description || locationLabel(next);
    const currentValues = entryForm.getFieldsValue(["city", "county", "state", "country"]);
    entryForm.setFieldsValue({
      city: next.city || currentValues.city || "",
      county: next.county || currentValues.county || "",
      state: next.state || currentValues.state || "",
      country: next.country || currentValues.country || "",
    });
    setEntryLocationQuery(query);
  };

  const renderEntryLocationSearch = () => {
    if (entryConfig !== "interestedLocations") return null;
    return (
      <div className="olreadia-flow-location-search">
        <Text className="olreadia-flow-upload-help">
          Search or enter places like city, county, state, country.
        </Text>
        <GooglePlacesAutocompleteInput
          googleMapsApiKey={googleMapsApiKey}
          placeholder="Search interested location"
          value={entryLocationQuery}
          onChange={setEntryLocationQuery}
          types={["(regions)"]}
          extraOptions={popularLocationOptions}
          onResolvePlace={resolveInterestedLocation}
        />
      </div>
    );
  };

  const itemForUploadedEntryFile = (section, values, uploadedFileId, file) => {
    if (section === "photos") return String(uploadedFileId);
    if (section === "documents") {
      return compactObject({
        ...values,
        id: String(uploadedFileId),
        caption: values.caption || file?.name || "Uploaded document",
      });
    }
    if (section === "certifications") {
      return compactObject({
        ...values,
        credentialType: "CERTIFICATION",
        fileId: String(uploadedFileId),
      });
    }
    if (section === "licenses") {
      return compactObject({
        ...values,
        credentialType: "LICENSE",
        fileId: String(uploadedFileId),
      });
    }
    return null;
  };

  const patchUploadedEntryFile = async (uploadedFileId, file) => {
    if (!["documents", "photos", "certifications", "licenses"].includes(entryConfig) || !profile.id) {
      return;
    }

    const values = compactObject(entryForm.getFieldsValue(true));
    const item = itemForUploadedEntryFile(entryConfig, values, uploadedFileId, file);
    if (!item) return;

    const current = toList(profile[entryConfig]);
    const nextIndex = entryIndex == null ? current.length : entryIndex;
    const nextList = entryIndex == null
      ? [...current, item]
      : current.map((existing, index) => (index === entryIndex ? item : existing));
    const nextPhotoFileIds = entryConfig === "photos"
      ? Array.from(new Set([...toList(profile.photoFileIds).map(String), String(uploadedFileId)]))
      : profile.photoFileIds;
    const next = normalizeProfile({
      ...profile,
      [entryConfig]: nextList,
      photoFileIds: nextPhotoFileIds,
    }, selectedWorkspaceId);

    setProfile(next);
    if (entryIndex == null) setEntryIndex(nextIndex);
    const payload = buildProfileRequest({
      ...profile,
      [entryConfig]: nextList,
      photoFileIds: nextPhotoFileIds,
      workspaceId: profile.workspaceId || selectedWorkspaceId || null,
    });
    const saved = await olreadiaService.professionProfiles.patchSection(entryConfig, payload, actorId, actorId, {
      previousProfile: profile,
    });
    const normalized = normalizeProfile(saved || next, selectedWorkspaceId);
    setProfile(normalized);
    metadataForm.setFieldsValue(profileToMetadata(normalized));
  };

  const handleEntryFileUpload = async (field, file, callbacks = {}) => {
    const uploaderId = user?.id || user?.userId || actorId;
    if (!uploaderId) {
      const error = new Error("Missing uploader id");
      callbacks.onError?.(error);
      message.error(error.message);
      return;
    }

    try {
      const isPhoto = field.upload === "photo";
      const result = await uploadFile(uploaderId, file, {
        purpose: "OTHER",
        fileType: isPhoto ? "PHOTO" : "DOCUMENT",
        preserveOriginal: true,
        skipUserValidation: true,
        includeUserId: false,
        confirmUpload: false,
      });
      const uploadedFileId = result?.fileId || result?.file?.id || result?.file?.fileId;
      if (!uploadedFileId) throw new Error("Upload failed: missing file ID");

      const stagedFile = {
        fileId: String(uploadedFileId),
        fileName: file?.name || result?.meta?.filename || result?.file?.filename || "",
        mimeType: file?.type || result?.file?.contentType || "",
        uploadedAt: new Date().toISOString(),
      };
      const nextStagedUpload = isPhoto
        ? {
            fieldName: field.name,
            files: [
              ...(stagedEntryUploadRef.current?.fieldName === field.name ? toList(stagedEntryUploadRef.current.files) : []),
              stagedFile,
            ],
          }
        : {
            fieldName: field.name,
            fileId: stagedFile.fileId,
            fileName: stagedFile.fileName,
            mimeType: stagedFile.mimeType,
            uploadedAt: stagedFile.uploadedAt,
            files: [stagedFile],
          };
      const nextStagedFiles = toList(nextStagedUpload.files);
      entryForm.setFieldsValue({
        [field.name]: isPhoto
          ? nextStagedFiles.map((staged) => staged.fileId).join(",")
          : stagedFile.fileId,
        fileName: isPhoto && nextStagedFiles.length > 1
          ? `${nextStagedFiles.length} photos attached`
          : stagedFile.fileName,
        mimeType: stagedFile.mimeType,
        uploadedAt: stagedFile.uploadedAt,
      });
      stageEntryUpload({
        ...nextStagedUpload,
        fileId: isPhoto ? nextStagedFiles[0]?.fileId : stagedFile.fileId,
        fileName: isPhoto && nextStagedFiles.length > 1 ? `${nextStagedFiles.length} photos attached` : stagedFile.fileName,
        mimeType: stagedFile.mimeType,
        uploadedAt: stagedFile.uploadedAt,
      });
      callbacks.onSuccess?.(result);
      message.success(`${file?.name || (isPhoto ? "Photo" : "File")} attached. Press Save to update the profile.`);
    } catch (uploadError) {
      callbacks.onError?.(uploadError);
      message.error(uploadError?.message || "Failed to upload file");
    }
  };

  const renderEntryField = (field) => {
    const rules = field.required ? [{ required: true, message: `${field.label} is required` }] : undefined;

    if (field.upload) {
      const isPhoto = field.upload === "photo";
      const icon = isPhoto ? <FileImageOutlined /> : <FileTextOutlined />;
      return (
        <Form.Item
          key={field.name}
          label={field.label}
          required={field.required}
          className="olreadia-flow-upload-field"
        >
          <Space.Compact className="olreadia-flow-upload-control">
            <Form.Item name={field.name} hidden rules={rules}>
              <Input />
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const uploadedValue = getFieldValue(field.name);
                const fileName = getFieldValue("fileName");
                return (
                  <Input
                    readOnly
                    prefix={icon}
                    value={fileName || (uploadedValue ? `${isPhoto ? "Photo" : "File"} uploaded` : "")}
                    placeholder={field.placeholder || `No ${isPhoto ? "photo" : "file"} uploaded yet`}
                  />
                );
              }}
            </Form.Item>
            <Upload
              showUploadList={false}
              accept={isPhoto ? "image/*" : undefined}
              multiple={isPhoto}
              customRequest={({ file, onSuccess, onError }) =>
                handleEntryFileUpload(field, file, { onSuccess, onError })
              }
            >
              <Button loading={fileUploading} icon={<UploadOutlined />}>
                {field.uploadLabel || "Upload"}
              </Button>
            </Upload>
          </Space.Compact>
          <Form.Item name="fileName" hidden><Input /></Form.Item>
          <Form.Item name="mimeType" hidden><Input /></Form.Item>
          <Form.Item name="uploadedAt" hidden><Input /></Form.Item>
          <Text className="olreadia-flow-upload-help">
            {isPhoto ? "Choose a photo from camera/gallery or image files." : "Choose a document file to attach to this profile."}
          </Text>
        </Form.Item>
      );
    }

    if (field.locationSearch) {
      return (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          rules={rules}
        >
          <GooglePlacesAutocompleteInput
            googleMapsApiKey={googleMapsApiKey}
            placeholder={field.placeholder || "Search location"}
            types={["(cities)"]}
            extraOptions={popularLocationOptions}
            onChange={(value) => setEntryLocationQuery(value)}
            onResolvePlace={(payload) => resolveRegionLocation(payload, field.name)}
          />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        key={field.name}
        name={field.name}
        label={field.label}
        valuePropName={field.checkbox ? "checked" : "value"}
        rules={rules}
      >
        {field.checkbox ? (
          <Switch />
        ) : field.options ? (
          <Select
            showSearch
            placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
            options={field.options.map((option) => ({ value: option, label: titleCase(option) }))}
            optionFilterProp="label"
          />
        ) : field.multiline ? (
          <TextArea rows={3} placeholder={field.placeholder} />
        ) : (
          <Input
            type={field.inputType === "date" ? "date" : field.inputType === "year" ? "number" : undefined}
            min={field.inputType === "year" ? 1900 : undefined}
            max={field.inputType === "year" ? 2100 : undefined}
            placeholder={field.placeholder}
          />
        )}
      </Form.Item>
    );
  };

  const deleteProfile = async () => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    if (!profile.id) return;
    try {
      setSaving(true);
      await olreadiaService.professionProfiles.delete(profile.id, { userId: actorId });
      message.success("Professional profile deleted.");
      navigate(paths.professionalProfiles);
    } catch (nextError) {
      message.error(nextError?.message || "Could not delete professional profile.");
    } finally {
      setSaving(false);
    }
  };

  const shareProfile = async () => {
    const url = `${window.location.origin}${paths.professionalProfileDetails(profile.id)}`;
    try {
      await navigator.clipboard?.writeText(url);
      message.success("Profile link copied.");
    } catch {
      message.info(url);
    }
  };

  const resolveFileMetadata = useCallback(async (fileId) => {
    const key = String(fileId || "").trim();
    if (!key || /^https?:\/\//i.test(key) || !getFileMetadata) return null;
    if (fileMetadataById[key]) return fileMetadataById[key];
    try {
      const [metadata, signedUrl] = await Promise.all([
        getFileMetadata(key).catch(() => null),
        getFileSignedUrl ? getFileSignedUrl(key).catch(() => "") : Promise.resolve(""),
      ]);
      const resolved = {
        ...(metadata || {}),
        ...(signedUrl ? { signedUrl } : {}),
      };
      setFileMetadataById((current) => ({ ...current, [key]: resolved }));
      return resolved;
    } catch {
      setFileMetadataById((current) => ({ ...current, [key]: { unavailable: true } }));
      return null;
    }
  }, [fileMetadataById, getFileMetadata, getFileSignedUrl]);

  const openMediaPreview = useCallback(async (item, label, type = "document", section = "") => {
    const fileId = mediaFileId(item);
    const directUrl = mediaUrl(item);
    const metadata = fileId ? await resolveFileMetadata(fileId) : null;
    const previewUrl = directUrl || mediaUrl(metadata);
    setMediaPreview({
      title: label || mediaTitle(item, type),
      type,
      section,
      item,
      fileId,
      url: previewUrl,
      metadata,
    });
  }, [resolveFileMetadata]);

  if (loading) {
    return (
      <div className={`olreadia-flow-page${embedded ? " olreadia-flow-page--embedded" : ""}`}>
        <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 8 }} /></Card>
      </div>
    );
  }

  const splitMediaPreview = mediaPreview && ["certifications", "licenses", "documents"].includes(mediaPreview.section);
  const resumeName = [profile.firstName, profile.middleName, profile.lastName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  const resumeLocation = [profile.city, profile.state, profile.country]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");
  const hasResumeContact = Boolean(profile.profilePhoto || resumeName || profile.email || profile.phone || resumeLocation);

  return (
    <div className={`olreadia-flow-page${embedded ? " olreadia-flow-page--embedded" : ""}`}>
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><IdcardOutlined /></span>
            <div className="olreadia-flow-profile-hero-copy">
              <Text className="olreadia-flow-eyebrow">{isNew ? "Add profile" : "Professional profile"}</Text>
              <Title level={2}>{profileTitle(profile)}</Title>
              <Paragraph className="olreadia-flow-profile-description">
                Manage profile details, sections, services, readiness, and hiring verification.
              </Paragraph>
            </div>
            {!readOnly ? (
              <Space wrap>
                {!isNew && (
                  <Button icon={<ShareAltOutlined />} onClick={shareProfile}>Share</Button>
                )}
                {canEditProfile ? (
                  <Button
                    className="olreadia-flow-button olreadia-flow-button--blue"
                    icon={<SafetyCertificateOutlined />}
                    onClick={() => (isAuthenticated ? navigate(paths.backgroundChecks) : requireSignIn())}
                  >
                    Background Checks
                  </Button>
                ) : null}
                {profileEditMode ? (
                  <Button
                    className="olreadia-flow-button olreadia-flow-button--danger"
                    icon={<CloseOutlined />}
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                ) : null}
                {canEditProfile ? (
                  <Button
                    type="primary"
                    icon={profileEditMode ? <CheckOutlined /> : <EditOutlined />}
                    onClick={() => {
                      if (!isAuthenticated) {
                        requireSignIn();
                        return;
                      }
                      if (profileEditMode) {
                        if (isNew) saveMetadata();
                        else setEditMode(false);
                        return;
                      }
                      setEditMode(true);
                    }}
                    loading={saving}
                  >
                    {profileEditMode ? "Done" : "Edit"}
                  </Button>
                ) : null}
              </Space>
            ) : null}
          </section>

          {hasResumeContact ? (
            <section className="olreadia-flow-resume-contact" aria-label="Professional contact details">
              {profile.profilePhoto ? (
                <Avatar className="olreadia-flow-resume-contact__photo" size={72} src={profile.profilePhoto}>
                  {resumeName?.charAt(0)}
                </Avatar>
              ) : null}
              <div className="olreadia-flow-resume-contact__body">
                {resumeName ? <Title level={3}>{resumeName}</Title> : null}
                <div className="olreadia-flow-resume-contact__details">
                  {profile.email ? <span><MailOutlined />{profile.email}</span> : null}
                  {profile.phone ? <span><PhoneOutlined />{profile.phone}</span> : null}
                  {resumeLocation ? <span><EnvironmentOutlined />{resumeLocation}</span> : null}
                </div>
              </div>
            </section>
          ) : null}

          {error ? <Alert type="error" showIcon message="Unable to load profile" description={error} style={{ marginBottom: 12 }} /> : null}

          <Card className="olreadia-flow-card olreadia-flow-overview-card">
            <div className="olreadia-flow-profile-progress">
              <div>
                <Text className="olreadia-flow-eyebrow">Profile completion</Text>
                <Title level={4} style={{ color: profileCompletionColor }}>{completion}% complete</Title>
              </div>
              <Progress type="circle" percent={completion} strokeColor={profileCompletionColor} size={76} />
            </div>
            {isNew ? (
              <Form
                form={metadataForm}
                layout="vertical"
                disabled={!profileEditMode}
                className="olreadia-flow-profile-metadata-form"
              >
                <div className="olreadia-flow-profile-form-heading">
                  <div>
                    <Text className="olreadia-flow-eyebrow">Profile basics</Text>
                    <Title level={5}>Create a professional profile employers can quickly understand</Title>
                  </div>
                  <Text>
                    Keep the essentials clear, reusable, and ready for verification.
                  </Text>
                </div>
                <div className="olreadia-flow-form-grid">
                  <Form.Item name="sectorCode" label="Profession sector" rules={[{ required: true, message: "Select a sector" }]}>
                    <Select
                      showSearch
                      options={sectorOptions}
                      placeholder="Search profession e.g. nursing, accounting"
                      optionFilterProp="label"
                    />
                  </Form.Item>
                  <Form.Item name="profileName" label="Profile name" rules={[{ required: true, message: "Enter a profile name" }]}>
                    <Input placeholder={isGeneral ? "General Professional Profile" : "e.g., Care Giver Professional Profile"} />
                  </Form.Item>
                  <Form.Item name="educationLevel" label="Education level">
                    <Select
                      placeholder="Select education level"
                      options={EDUCATION_LEVELS.map((level) => ({ value: level, label: titleCase(level) }))}
                    />
                  </Form.Item>
                  <Form.Item name="isPrivate" label="Private profile" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </div>
                <Form.Item
                  name="profileSummary"
                  label="Profile summary"
                  className="olreadia-flow-summary-editor-field"
                >
                  <RichTextEditor
                    readOnly={!profileEditMode}
                    height="190px"
                    placeholder="Summarize your experience, specialties, strengths, and the services you provide."
                  />
                </Form.Item>
              </Form>
            ) : (
              <MetadataOverview
                profileEditMode={profileEditMode}
                profile={profile}
                onEdit={openFieldUpdate}
              />
            )}
          </Card>

          <div className="olreadia-flow-profile-sections">
            <ProfileSectionGroup
              title="Career"
              description="Work history, education, and certifications that help hiring teams understand your professional background."
            >
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="workExperienceHistory" onAdd={openEntryModal} onRemove={removeEntry} />
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="education" onAdd={openEntryModal} onRemove={removeEntry} />
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="certifications" onAdd={openEntryModal} onRemove={removeEntry} onPreview={openMediaPreview} fileMetadataById={fileMetadataById} />
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="licenses" onAdd={openEntryModal} onRemove={removeEntry} onPreview={openMediaPreview} fileMetadataById={fileMetadataById} />
            </ProfileSectionGroup>

            <ProfileSectionGroup
              title="Readiness"
              description="Regions, references, documents, and photos that make the profile easier to verify and reuse."
            >
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="previousRegionsWorkedIn" onAdd={openEntryModal} onRemove={removeEntry} />
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="references" onAdd={openEntryModal} onRemove={removeEntry} />
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="documents" onAdd={openEntryModal} onRemove={removeEntry} onPreview={openMediaPreview} fileMetadataById={fileMetadataById} />
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="photos" onAdd={openEntryModal} onRemove={removeEntry} onPreview={openMediaPreview} fileMetadataById={fileMetadataById} />
            </ProfileSectionGroup>

            <ProfileSectionGroup
              title="Services & Locations"
              description="Services, shifts, and locations you want this profile to represent."
            >
              <Card className="olreadia-flow-card olreadia-flow-profile-section-card">
                <Title level={4}>Interested Services</Title>
                <Select
                  mode="multiple"
                  disabled={!profileEditMode}
                  value={selectedServiceValues}
                  options={serviceOptions}
                  placeholder="Search services e.g. home care, tutoring"
                  onChange={setServicesForProfile}
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                />
              </Card>
              <Card className="olreadia-flow-card olreadia-flow-profile-section-card">
                <Title level={4}>Interested Shifts</Title>
                <Checkbox.Group
                  disabled={!profileEditMode}
                  value={toList(profile.interestedShifts)}
                  options={SHIFTS.map((shift) => ({ value: shift, label: titleCase(shift) }))}
                  onChange={setShiftsForProfile}
                />
              </Card>
              <SectionList profileEditMode={profileEditMode} profile={profile} fieldKey="interestedLocations" onAdd={openEntryModal} onRemove={removeEntry} />
            </ProfileSectionGroup>
          </div>

          {!isNew && profileEditMode ? (
            <Card className="olreadia-flow-card">
              <Space wrap>
                <Popconfirm title="Delete professional profile?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={deleteProfile}>
                  <Button danger icon={<DeleteOutlined />} loading={saving}>Delete Profile</Button>
                </Popconfirm>
              </Space>
            </Card>
          ) : null}
        </main>

        {!embedded && !hidePromotions ? <SidePromotionSection className="olreadia-flow-promotions" /> : null}
      </div>

      <Modal
        open={Boolean(entryConfig)}
        title={entryConfig ? `${entryIndex == null ? "Add" : "Edit"} ${FIELD_CONFIG[entryConfig].title}` : ""}
        onCancel={closeEntryModal}
        onOk={saveEntry}
        okText="Save"
        confirmLoading={saving}
        destroyOnClose
      >
        {entryConfig ? (
          <Form form={entryForm} layout="vertical" className="olreadia-flow-profile-edit-form">
            {FIELD_CONFIG[entryConfig].help ? (
              <Text className="olreadia-flow-field-help">{FIELD_CONFIG[entryConfig].help}</Text>
            ) : null}
            {renderEntryLocationSearch()}
            {FIELD_CONFIG[entryConfig].fields.map((field) => {
              const isCurrent = Boolean(entryForm.getFieldValue("isCurrent") || entryForm.getFieldValue("current"));
              if (field.hideWhenCurrent && isCurrent) return null;
              return renderEntryField(field);
            })}
          </Form>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(fieldUpdateKind)}
        title={fieldUpdateKind ? `Update ${fieldLabel(fieldUpdateKind)}` : "Update profile"}
        onCancel={closeFieldUpdate}
        onOk={saveFieldUpdate}
        okText="Done"
        confirmLoading={saving}
        destroyOnClose
      >
        {fieldUpdateKind ? (
          <Form form={fieldUpdateForm} layout="vertical" className="olreadia-flow-profile-edit-form">
            <Form.Item
              name={fieldUpdateKind}
              label={fieldLabel(fieldUpdateKind)}
              className={fieldUpdateKind === "profileSummary" ? "olreadia-flow-summary-editor-field" : undefined}
              valuePropName={fieldUpdateKind === "isPrivate" ? "checked" : "value"}
              rules={["sectorCode", "profileName"].includes(fieldUpdateKind) ? [{ required: true, message: `${fieldLabel(fieldUpdateKind)} is required` }] : undefined}
            >
              {fieldUpdateKind === "sectorCode" ? (
                <Select
                  showSearch
                  options={sectorOptions}
                  placeholder="Search profession e.g. nursing, accounting"
                  optionFilterProp="label"
                />
              ) : fieldUpdateKind === "educationLevel" ? (
                <Select
                  placeholder="Select education level"
                  options={EDUCATION_LEVELS.map((level) => ({ value: level, label: titleCase(level) }))}
                />
              ) : fieldUpdateKind === "profileSummary" ? (
                <RichTextEditor
                  height="190px"
                  placeholder="Summarize your experience, specialties, strengths, and the services you provide."
                />
              ) : fieldUpdateKind === "isPrivate" ? (
                <Switch />
              ) : (
                <Input placeholder="e.g., Care Giver Professional Profile" />
              )}
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
      <Modal
        open={Boolean(mediaPreview)}
        title={mediaPreview?.title || "Review file"}
        onCancel={() => setMediaPreview(null)}
        width={splitMediaPreview ? "100vw" : 760}
        style={splitMediaPreview ? { margin: 0, maxWidth: "100vw", paddingBottom: 0, top: 0 } : undefined}
        bodyStyle={splitMediaPreview ? { height: "calc(100vh - 55px)", padding: "8px 14px 0", overflow: "hidden" } : undefined}
        footer={splitMediaPreview
          ? null
          : [<Button key="close" onClick={() => setMediaPreview(null)}>Done</Button>]}
        className="olreadia-flow-media-modal"
        wrapClassName={splitMediaPreview ? "olreadia-flow-media-modal-wrap" : undefined}
      >
        {mediaPreview ? (
          <div className={`olreadia-flow-media-preview${splitMediaPreview ? " olreadia-flow-media-preview--credential" : ""}`}>
            <div className="olreadia-flow-media-preview__details">
              <div>
                <Text>{mediaPreview.section === "certifications" ? "Certificate" : mediaPreview.section === "licenses" ? "License" : "File"}</Text>
                <strong>{mediaPreview.title}</strong>
              </div>
              {["certifications", "licenses"].includes(mediaPreview.section) ? (
                <CredentialPreviewDetails credential={mediaPreview.item} section={mediaPreview.section} />
              ) : mediaPreview.section === "documents" ? (
                <DocumentPreviewDetails document={mediaPreview.item} metadata={mediaPreview.metadata} />
              ) : null}
              {!mediaPreview.url ? (
                <Alert
                  type="info"
                  showIcon
                  message="Preview not available"
                  description="This file is attached to the profile, but no viewable URL was returned yet."
                />
              ) : null}
            </div>
            <div className="olreadia-flow-media-preview__document">
              {mediaPreview.url && (mediaPreview.type === "photo" || isImageLike(mediaPreview.url)) ? (
                <img src={mediaPreview.url} alt="" />
              ) : mediaPreview.url ? (
                <iframe src={mediaPreview.url} title={mediaPreview.title || "Document preview"} />
              ) : (
                <div className="olreadia-flow-media-preview__fallback">
                  {mediaPreview.type === "photo" ? <FileImageOutlined /> : <FileTextOutlined />}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Modal>
      <SignInRequiredModal
        open={signInPromptOpen}
        onClose={() => setSignInPromptOpen(false)}
        title="Login required"
        message="You can view this professional profile without logging in. Log in or create an account to edit, request checks, or continue."
      />
    </div>
  );
}

function profileToMetadata(profile) {
  return {
    sectorCode: getSectorCode(profile.sector || profile.sectorCode),
    profileName: profile.profileName || profileTitle(profile),
    educationLevel: profile.educationLevel || "CERTIFICATE",
    isPrivate: Boolean(profile.isPrivate),
    profileSummary: profile.profileSummary || "",
  };
}

function fieldLabel(kind) {
  switch (kind) {
    case "sectorCode":
      return "Line of Profession";
    case "profileName":
      return "Professional Profile Name";
    case "profileSummary":
      return "Professional Profile Summary";
    case "educationLevel":
      return "Education Level";
    case "isPrivate":
      return "Private Profile";
    default:
      return titleCase(kind);
  }
}

function MetadataOverview({ profileEditMode, profile, onEdit }) {
  const renderSummary = () => {
    if (!hasRichText(profile.profileSummary)) {
      return <strong>No profile summary added yet.</strong>;
    }
    return (
      <div
        className="olreadia-rich-text olreadia-flow-profile-summary-view"
        dangerouslySetInnerHTML={richTextMarkup(profile.profileSummary)}
      />
    );
  };

  const rows = [
    {
      key: "sectorCode",
      label: "Line of Profession",
      value: getSectorName(profile.sector || profile.sectorCode),
    },
    {
      key: "profileName",
      label: "Professional Profile Name",
      value: profile.profileName || profileTitle(profile),
    },
    {
      key: "educationLevel",
      label: "Education Level",
      value: titleCase(profile.educationLevel || "Not set"),
    },
    {
      key: "isPrivate",
      label: "Private Profile",
      value: profile.isPrivate ? "Private" : "Visible",
    },
    {
      key: "profileSummary",
      label: "Professional Profile Summary",
      value: renderSummary(),
      wide: true,
    },
  ];

  return (
    <div className="olreadia-flow-update-list">
      {rows.map((row) => (
        <div key={row.key} className={`olreadia-flow-update-row${row.wide ? " olreadia-flow-update-row--wide" : ""}`}>
          <div>
            <Text>{row.label}</Text>
            {row.key === "profileSummary" ? row.value : <strong>{row.value}</strong>}
          </div>
          {profileEditMode ? (
            <Button
              size="small"
              className="olreadia-flow-row-action olreadia-flow-row-action--edit"
              icon={<EditOutlined />}
              onClick={() => onEdit(row.key)}
            >
              Update
            </Button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ProfileSectionGroup({ title, description, children }) {
  return (
    <section className="olreadia-flow-section-group">
      <div className="olreadia-flow-section-group__header">
        <Title level={3}>{title}</Title>
        <Text>{description}</Text>
      </div>
      <div className="olreadia-flow-profile-section-grid">
        {children}
      </div>
    </section>
  );
}

function PreviewDetailRows({ rows }) {
  const visibleRows = rows.filter(([, value]) => typeof value === "string" ? value.trim() : value);
  if (!visibleRows.length) return null;

  return (
    <>
      {visibleRows.map(([label, value]) => (
        <div key={label}>
          <Text>{label}</Text>
          <strong>{value}</strong>
        </div>
      ))}
    </>
  );
}

function CredentialPreviewDetails({ credential, section }) {
  if (!credential || typeof credential !== "object") return null;
  const labelPrefix = section === "licenses" ? "License" : "Certification";
  const rows = [
    [`${labelPrefix} Name`, credential.name],
    [`${labelPrefix} Number`, credential.certificationNumber],
    ["Issuer", credential.issuer],
    ["Issue Date", credential.issueDate],
    ["Expiry Date", credential.expiryDate],
  ];

  return <PreviewDetailRows rows={rows} />;
}

function DocumentPreviewDetails({ document }) {
  const doc = document && typeof document === "object" ? document : {};
  const rows = [
    ["Caption", doc.caption],
  ];

  return <PreviewDetailRows rows={rows} />;
}

const mediaUrl = (value) => {
  if (typeof value === "string") return isOpenableMediaUrl(value) ? value.trim() : "";
  if (!value || typeof value !== "object") return "";
  return [
    value.publicUrl,
    value.publicURL,
    value.signedUrl,
    value.signedURL,
    value.downloadUrl,
    value.downloadURL,
    value.fileUrl,
    value.fileURL,
    value.url,
    value.uri,
    value.localUri,
    value.fileLocalUri,
    isOpenableMediaUrl(value.fileId) ? value.fileId : "",
    isOpenableMediaUrl(value.id) ? value.id : "",
  ].find((item) => typeof item === "string" && item.trim()) || "";
};

const mediaFileId = (value) => {
  if (typeof value === "string") return extractFileId(value);
  if (!value || typeof value !== "object") return "";
  return extractFileId(value.fileId || value.id || value.uuid || value.documentId || value.photoId);
};

const mediaTitle = (value, type = "document", index = 0) => {
  if (typeof value === "string") return type === "photo" ? `Photo ${index + 1}` : value;
  if (!value || typeof value !== "object") return type === "photo" ? `Photo ${index + 1}` : "File";
  return [
    value.caption,
    value.name,
    value.fileName,
    value.filename,
    value.documentName,
    value.title,
    value.certificationName,
    type === "photo" ? `Photo ${index + 1}` : "",
  ].find((item) => typeof item === "string" && item.trim()) || (type === "photo" ? `Photo ${index + 1}` : "File");
};

const isImageLike = (value = "") => /\.(png|jpe?g|webp|gif|heic|heif)(\?|#|$)/i.test(String(value || ""));

function SectionList({ profileEditMode, profile, fieldKey, onAdd, onRemove, onPreview, fileMetadataById }) {
  const config = FIELD_CONFIG[fieldKey];
  const items = toList(profile[fieldKey]);
  const mediaType = fieldKey === "photos" ? "photo" : ["documents", "certifications", "licenses"].includes(fieldKey) ? "document" : "";
  return (
    <Card className="olreadia-flow-card olreadia-flow-profile-section-card">
      <div className="olreadia-flow-section-heading">
        <div>
          <Title level={4}>{config.title}</Title>
          {profileEditMode ? <Text className="olreadia-flow-section-hint">Updates patch this section when you press Done.</Text> : null}
        </div>
        {profileEditMode ? (
          <Button
            size="small"
            className="olreadia-flow-section-add"
            icon={<PlusOutlined />}
            onClick={() => onAdd(fieldKey)}
          >
            Add
          </Button>
        ) : null}
      </div>
      {items.length ? (
        <div className={`olreadia-flow-nested-list${mediaType === "photo" ? " olreadia-flow-photo-grid" : ""}`}>
          {items.map((item, index) => (
            mediaType ? (
              <MediaListItem
                key={`${fieldKey}-${index}`}
                item={item}
                index={index}
                type={mediaType}
                profileEditMode={profileEditMode}
                metadata={fileMetadataById?.[mediaFileId(item)]}
                title={mediaTitle(item, mediaType, index)}
                meta={config.itemMeta(item)}
                onPreview={() => onPreview?.(item, mediaTitle(item, mediaType, index), mediaType, fieldKey)}
                onEdit={() => onAdd(fieldKey, item, index)}
                onRemove={() => onRemove(fieldKey, index)}
              />
            ) : (
              <div key={`${fieldKey}-${index}`} className="olreadia-flow-nested-row">
                <span><IdcardOutlined /></span>
                <span>
                  <strong>{config.itemTitle(item)}</strong>
                  {config.itemMeta(item) ? <Text>{config.itemMeta(item)}</Text> : null}
                </span>
                {profileEditMode ? (
                  <Space>
                    <Button
                      size="small"
                      className="olreadia-flow-row-action olreadia-flow-row-action--edit"
                      onClick={() => onAdd(fieldKey, item, index)}
                    >
                      Edit
                    </Button>
                    <Button size="small" danger onClick={() => onRemove(fieldKey, index)}>Remove</Button>
                  </Space>
                ) : null}
              </div>
            )
          ))}
        </div>
      ) : (
        <Text type="secondary">{config.empty}</Text>
      )}
    </Card>
  );
}

function MediaListItem({ item, index, type, title, meta, metadata, profileEditMode, onPreview, onEdit, onRemove }) {
  const directUrl = mediaUrl(item);
  const resolvedUrl = directUrl || mediaUrl(metadata);
  const fileId = mediaFileId(item);
  const imagePreview = type === "photo" || isImageLike(resolvedUrl) || isImageLike(title);
  const canPreview = Boolean(resolvedUrl || fileId);
  const content = (
    <>
      <span className="olreadia-flow-media-thumb">
        {resolvedUrl && imagePreview ? (
          <img src={resolvedUrl} alt="" />
        ) : (
          type === "photo" ? <FileImageOutlined /> : <FileTextOutlined />
        )}
      </span>
      <span className="olreadia-flow-media-copy">
        <strong>{title}</strong>
        <Text>{meta || (fileId ? "File attached" : "Preview unavailable")}</Text>
      </span>
    </>
  );

  if (type === "photo") {
    return (
      <div className="olreadia-flow-photo-tile">
        <button
          type="button"
          className="olreadia-flow-photo-tile__preview"
          onClick={canPreview ? onPreview : undefined}
          disabled={!canPreview}
          aria-label={`Review ${title || `photo ${index + 1}`}`}
        >
          {resolvedUrl ? <img src={resolvedUrl} alt="" /> : <FileImageOutlined />}
        </button>
        {profileEditMode ? (
          <Popconfirm
            title="Remove this file?"
            description="This will remove the file from this professional profile."
            okText="Remove"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={onRemove}
          >
            <button type="button" className="olreadia-flow-photo-tile__remove" aria-label={`Remove ${title}`}>
              <CloseOutlined />
            </button>
          </Popconfirm>
        ) : null}
      </div>
    );
  }

  return (
    <div className="olreadia-flow-nested-row olreadia-flow-media-row">
      {content}
      <Space>
        <Button
          size="small"
          icon={<EyeOutlined />}
          className="olreadia-flow-row-action olreadia-flow-row-action--edit"
          onClick={onPreview}
          disabled={!canPreview}
        >
          View
        </Button>
        {profileEditMode ? (
          <>
            <Button size="small" className="olreadia-flow-row-action olreadia-flow-row-action--edit" onClick={onEdit}>
              Edit
            </Button>
            <Popconfirm
              title="Remove this file?"
              description="This will remove the file from this professional profile."
              okText="Remove"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={onRemove}
            >
              <Button size="small" danger>Remove</Button>
            </Popconfirm>
          </>
        ) : null}
      </Space>
    </div>
  );
}
